import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const DeleteConfirmModal = ({ open, handleClose, tenant, handleDeleteConfirm }) => {
    const [loading, setLoading] = useState(false);

    if (!tenant) return null;

    const handleConfirmDelete = async () => {
        setLoading(true);
        try {
            await handleDeleteConfirm(tenant.id);
            handleClose();
        } catch (error) {
            console.error('Error deleting tenant:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    p: 2.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                    Delete Tenant
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    disabled={loading}
                    size="small"
                    sx={{
                        color: 'white',
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.25)',
                        },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3, backgroundColor: '#f8fafc', textAlign: 'center' }}>
                {/* Warning Icon */}
                <Avatar
                    sx={{
                        width: 60,
                        height: 60,
                        backgroundColor: '#ef4444',
                        margin: '0 auto 1.5rem',
                        fontSize: '1.75rem',
                    }}
                >
                    ⚠
                </Avatar>

                {/* Tenant Card */}
                <Box
                    sx={{
                        backgroundColor: 'white',
                        borderRadius: 1,
                        p: 2,
                        mb: 2,
                        border: '1px solid #e5e7eb',
                    }}
                >
                    <Avatar
                        sx={{
                            width: 50,
                            height: 50,
                            backgroundColor: '#ef4444',
                            margin: '0 auto 1rem',
                            fontSize: '1.25rem',
                            color: 'white',
                            fontWeight: 600,
                        }}
                    >
                        {tenant.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
                        {tenant.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {tenant.email}
                    </Typography>
                </Box>

                {/* Warning Message */}
                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: '0.875rem', color: '#1f2937', mb: 1 }}>
                        Are you sure you want to delete this tenant?
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>
                        This action cannot be undone. The tenant account and all associated data will be permanently deleted.
                    </Typography>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        sx={{
                            textTransform: 'none',
                            borderRadius: 0.5,
                            px: 3,
                            py: 1,
                            backgroundColor: '#e5e7eb',
                            color: '#1f2937',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            '&:hover': {
                                backgroundColor: '#d1d5db',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        disabled={loading}
                        sx={{
                            textTransform: 'none',
                            borderRadius: 0.5,
                            px: 3,
                            py: 1,
                            backgroundColor: '#ef4444',
                            color: 'white',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            '&:hover': {
                                backgroundColor: '#dc2626',
                            },
                            '&:disabled': {
                                backgroundColor: '#fecaca',
                                color: 'white',
                            },
                        }}
                    >
                        {loading ? 'Deleting...' : 'Delete Tenant'}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default DeleteConfirmModal;
